import { useAuth } from "../lib/auth";
import { estadoWhatsapp } from "./HistoricoWhatsapp";
import { cursoCurto, dataHoraCurta, moeda } from "../lib/formato";
import { telefoneExibicao } from "../lib/paises";
import { COR_STATUS, MOTIVOS_NAO_MATRICULA, ROTULO_STATUS, type StatusVisita, type Visita } from "../lib/tipos";

export type Coluna = "responsavel" | "aluno" | "telefone" | "curso" | "serie" | "inicio" | "status" | "motivo" | "valor";

/**
 * Mostra "parcial / integral" quando a família pediu os dois orçamentos.
 * Valor zerado conta como ausente: proposta real nunca tem mensalidade zero,
 * e o 4.x às vezes gravava "0.00" no bloco que não foi preenchido.
 */
function valorComDesconto(v: Visita) {
  const num = (x: unknown) => {
    const n = Number(x ?? 0);
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  const parcial = num(v.proposta?.valorComDesconto);
  const integral = num(v.proposta?.valorComDescontoIntegral);

  const estilo: React.CSSProperties = { fontSize: 11, whiteSpace: "nowrap" };

  if (parcial === null && integral === null) return <span style={estilo}>—</span>;
  if (integral === null) return <span style={estilo} title="Período parcial">{moeda(parcial)}</span>;
  if (parcial === null) return <span style={estilo} title="Período integral">{moeda(integral)}</span>;

  return (
    <span style={estilo} title="Parcial / Integral">
      {moeda(parcial)}<span style={{ color: "var(--texto-suave)" }}> / </span>
      <span style={{ color: "#1570ef" }}>{moeda(integral)}</span>
    </span>
  );
}

/**
 * Largura máxima por coluna. Precisa ser aplicada num <div> dentro da célula:
 * em tabela de layout automático o navegador ignora max-width no <td> e
 * dimensiona pelo conteúdo — foi o que criava a barra de rolagem horizontal.
 */
const LARGURA: Record<Coluna, number> = {
  responsavel: 130,
  aluno: 130,
  telefone: 105,
  curso: 95,
  serie: 70,
  inicio: 92,
  status: 108,
  motivo: 115,
  valor: 158,
};

const CABECALHO: Record<Coluna, string> = {
  responsavel: "Visitante",
  aluno: "Aluno",
  telefone: "Telefone",
  curso: "Curso",
  serie: "Série",
  inicio: "Início",
  status: "Status",
  motivo: "Motivo",
  valor: "Valor c/ Desc.",
};

/** Texto puro da célula — vira o `title`, para ler o que as reticências cortam. */
function textoSimples(v: Visita, c: Coluna): string | undefined {
  switch (c) {
    case "responsavel": return v.nomeResponsavel;
    case "aluno": return v.nomeAluno;
    case "curso": return v.curso?.nome ?? undefined;
    case "serie": return v.serie?.nome ?? undefined;
    default: return undefined;
  }
}

function celula(v: Visita, c: Coluna) {
  switch (c) {
    /**
     * O selo SITE é permanente: ele diz de onde a visita veio, e isso não
     * muda depois que alguém abre. O que muda é a ênfase — verde cheio
     * enquanto ninguém olhou, contorno depois. Antes o selo sumia ao ser
     * aberto, e junto sumia a resposta para "quantas famílias chegam pelo
     * site?", que é a pergunta que justifica a página existir.
     */
    case "responsavel": {
      const novo = v.origemPublica && !v.vistaEm;
      return (
        <>
          {v.origemPublica && (
            <span
              title={novo ? "Agendou pelo site e ninguém da equipe abriu ainda" : "Esta família agendou pelo site"}
              style={{
                fontSize: 9, fontWeight: 700, borderRadius: 4, padding: "1px 4px",
                marginRight: 5, verticalAlign: 1,
                background: novo ? "#0e8c3a" : "transparent",
                color: novo ? "#fff" : "#0e8c3a",
                border: `1px solid ${novo ? "#0e8c3a" : "#c6e9d4"}`,
              }}
            >
              SITE
            </span>
          )}
          <strong>{v.nomeResponsavel}</strong>
        </>
      );
    }
    case "aluno": return v.nomeAluno;
    case "telefone": return telefoneExibicao(v.telefoneResponsavel, v.paisTelefone);
    case "curso": return cursoCurto(v.curso?.nome);
    case "serie": return v.serie?.nome ?? "—";
    case "inicio": return dataHoraCurta(v.inicioEm);
    case "valor": return valorComDesconto(v);
    case "motivo":
      return v.motivoNaoMatricula
        ? MOTIVOS_NAO_MATRICULA[v.motivoNaoMatricula] ?? v.motivoNaoMatricula
        : "Não informado";
    case "status":
      return (
        <span className="etiqueta" style={{ background: COR_STATUS[v.status as StatusVisita] }}>
          {ROTULO_STATUS[v.status as StatusVisita]}
        </span>
      );
  }
}

export function TabelaVisitas({
  visitas,
  colunas,
  carregando,
  vazio = "Nenhuma visita encontrada",
  podeEditar,
  onEditar,
  onFicha,
  onWhatsapp,
  onExcluir,
}: {
  visitas: Visita[];
  colunas: Coluna[];
  carregando?: boolean;
  vazio?: string;
  podeEditar: boolean;
  onEditar: (v: Visita) => void;
  onFicha: (v: Visita) => void;
  /** Quando presente, mostra o estado dos envios e abre o histórico ao clicar. */
  onWhatsapp?: (v: Visita) => void;
  /** Só administrador exclui — a lixeira nem aparece para os outros. */
  onExcluir?: (v: Visita) => void;
}) {
  const { usuario, podeAoMenos } = useAuth();
  const gestor = podeAoMenos("GESTOR");
  const admin = podeAoMenos("ADMIN");

  /**
   * Todos enxergam todas as visitas, mas consultor só edita as próprias.
   * Esconder o lápis evita a frustração de abrir, preencher e levar 403 —
   * quem decide de verdade continua sendo a API.
   */
  const editavel = (v: Visita) =>
    podeEditar && (gestor || v.responsavelId === usuario?.id || v.criadoPorId === usuario?.id);

  return (
    <div className="cartao" style={{ padding: 0, overflowX: "auto" }}>
      <table>
        <thead>
          <tr>
            {colunas.map((c) => (
              <th key={c} title={c === "valor" ? "Parcial / Integral — quando há os dois, a família pediu os dois orçamentos" : undefined}>
                {CABECALHO[c]}
              </th>
            ))}
            <th style={{ width: 60 + (onWhatsapp ? 30 : 0) + (admin && onExcluir ? 28 : 0) }}>Ações</th>
          </tr>
        </thead>
        <tbody>
          {visitas.map((v) => (
            <tr key={v.id}>
              {colunas.map((c) => (
                <td key={c} title={textoSimples(v, c)}>
                  <div
                    style={{
                      maxWidth: LARGURA[c],
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      ...(c === "curso" || c === "serie" ? { color: "var(--texto-suave)" } : {}),
                    }}
                  >
                    {celula(v, c)}
                  </div>
                </td>
              ))}
              <td style={{ whiteSpace: "nowrap", maxWidth: "none", overflow: "visible" }}>
                {editavel(v) && (
                  <button
                    onClick={() => onEditar(v)}
                    title="Editar visita"
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, padding: 4 }}
                  >
                    ✏️
                  </button>
                )}
                <button
                  onClick={() => onFicha(v)}
                  title="Imprimir ficha"
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, padding: 4 }}
                >
                  🖨️
                </button>
                {onWhatsapp && (() => {
                  const e = estadoWhatsapp(v);
                  return (
                    <button
                      onClick={() => onWhatsapp(v)}
                      title={`WhatsApp: ${e.texto}`}
                      style={{ background: "none", border: "none", cursor: "pointer", padding: 4, fontSize: 13, fontWeight: 700, color: e.cor, letterSpacing: -1 }}
                    >
                      💬<span style={{ fontSize: 11, marginLeft: 1 }}>{e.icone}</span>
                    </button>
                  );
                })()}
                {admin && onExcluir && (
                  <button
                    onClick={() => onExcluir(v)}
                    title="Excluir visita"
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: 15, padding: 4, opacity: .55 }}
                    onMouseEnter={(ev) => { ev.currentTarget.style.opacity = "1"; }}
                    onMouseLeave={(ev) => { ev.currentTarget.style.opacity = ".55"; }}
                  >
                    🗑️
                  </button>
                )}
              </td>
            </tr>
          ))}
          {!carregando && visitas.length === 0 && (
            <tr>
              <td colSpan={colunas.length + 1} style={{ textAlign: "center", color: "var(--texto-suave)", padding: 32 }}>
                {vazio}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
